import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
	updateAnswer,
	deleteEntity,
	ajaxRequest,
	addSectionHeading,
} from '~/api';
import {
	addLessonEntity,
	addSectionHeadingEntity,
	addTopicEntity,
	addQuizEntity,
	addFinalQuizEntity,
	addQuestionEntity,
	addAnswerEntity,
	setActiveLesson,
} from '~/redux/actions/workspaceActions';
import {
	addLesson,
	addTopic,
	addQuiz,
	addQuestion,
} from '~/redux/actions/dataActions';
import Icon from '~/components/common/icon';

/**
 * New entity creation. Allows you to create new lessons, topics and quizzes.
 * When the user clicks on the New Entity button (handleShowForm()), a request will be sent to create a temporary new entity
 * If they cancel (handleCancelAddNew()), the temporary new entity will be deleted
 * If they decide to add the new entity (handleSubmit()), the new entity will be renamed to the title they choose
 */
class NewEntity extends Component {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		// cancel token to abort any requests when unmounting the component.
		this.signal = axios.CancelToken.source();

		/**
		 * Our state which contains:
		 * - showForm: whether to show the edit/new form
		 * - value: value for the text input
		 * - is_loading: whether a request is currently processing
		 * - error: in case we have any errors from the AJAX endpoint
		 * - post_id: post id for new entity
		 * - view_url: url for viewing the entity
		 * - edit_url: url for editing the entity
		 */
		this.state = {
			showForm: false,
			value: '',
			is_loading: false,
			error: null,
			post_id: null,
			view_url: null,
			edit_url: null,
		};

		this.handleShowForm = this.handleShowForm.bind( this );
		this.handleChange = this.handleChange.bind( this );
		this.handleSubmit = this.handleSubmit.bind( this );
		this.handleCancelAddNew = this.handleCancelAddNew.bind( this );

		// to prevent memory leaks
		this._isMounted = false;
	}

	/**
	 * When component is mounted
	 */
	componentDidMount() {
		this._isMounted = true;
	}

	/**
	 * Cancel all requests when unmounting the component.
	 */
	componentWillUnmount() {
		this.signal.cancel();
		this._isMounted = false;
	}

	/**
	 * Handles the form submission. Should be replaced by calls to the API
	 *
	 * @param {handleSubmit} e
	 */
	async handleSubmit( e ) {
		e.preventDefault();

		const { value, post_id, view_url, edit_url } = this.state;
		const {
			workspace,
			type,
			parentLesson,
			setActiveLesson,
			addLessonEntity,
			addSectionHeadingEntity,
			addTopicEntity,
			addQuizEntity,
			addQuestionEntity,
			addAnswerEntity,
			addFinalQuizEntity,
			position,
			addLesson,
			addTopic,
			addQuiz,
			addQuestion,
		} = this.props;

		if ( value && post_id ) {
			if ( this._isMounted ) {
				// Set the loading state.
				this.setState( {
					is_loading: true,
					error: null,
				} );
			}

			// Adding a lesson, topic, quiz or question
			if ( 'answer' !== type && 'section-heading' !== type ) {
				// Post data
				const payload = {
					action: 'learndash_builder_selector_step_title',
					builder_data:
						learndash_builder_assets[
							LearnDashData.post_data.builder_post_type
						].post_data,
					builder_query_args: {
						new_title: value,
						post_id,
						post_type: LearnDashData.sfwdMap[ type ],
					},
				};

				const res = await ajaxRequest( payload, {
					cancelToken: this.signal.token,
				} );

				this.setState( {
					is_loading: false,
				} );

				if ( res.data.status ) {
					// Build the new entity object to add to our app workspace and data
					const entity = {
						ID: post_id,
						post_title: value,
						type,
						url: view_url,
						edit_link: edit_url,
					};

					// Check if we have a parent node to set the active entity
					if ( parentLesson ) {
						setActiveLesson( {
							activeLesson: parentLesson,
						} );
					}

					if ( 'lesson' === type ) {
						addLessonEntity( entity ); // Adds to the workspace
						addLesson( entity ); // Adds to the data
					} else if ( 'topic' === type ) {
						addTopicEntity( parentLesson, null, entity ); // Adds to the workspace
						addTopic( entity ); // Adds to the data
					} else if ( 'quiz' === type ) {
						// If adding from final quiz area, add as final quiz
						if ( 'footer' === position ) {
							addFinalQuizEntity(
								workspace.present.quizzes.length,
								entity
							); // Adds to the workspace
						} else {
							addQuizEntity( parentLesson, null, entity ); // Adds to the workspace
						}
						addQuiz( entity ); // Adds to the data
					} else if ( 'question' === type ) {
						addQuestionEntity( entity ); // Adds to the workspace
						addQuestion( entity ); // Adds to the data
					}

					if ( this._isMounted ) {
						this.setState( {
							showForm: false,
							value: '',
						} );
					}
				} else if ( this._isMounted ) {
					this.setState( {
						error:
							'An error occurred while submitting your request. Please try again.',
					} );
				}
			}
		} else if ( value && 'section-heading' === type ) {
			// Adding a section heading
			let currentSections =
				workspace.present.sections &&
				0 < workspace.present.sections.length
					? workspace.present.sections
					: [];
			const currentID = LearnDashData.post_data.builder_post_id; // get current post ID
			const sectionID = new Date().getTime(); // generate an unique ID for the section heading
			const activeLessonIndex = workspace.present.lessons.findIndex(
				( node ) => node.ID === workspace.present.activeLesson
			);
			const newSectionIndex =
				-1 !== activeLessonIndex
					? parseInt( activeLessonIndex + 1, 10 )
					: workspace.present.lessons.length; // Adds new section after active lesson, or after existing heading if there is no lesson

			// Build the new section heading entity object to add to our app workspace and data
			const entity = {
				order: newSectionIndex,
				ID: sectionID,
				post_title: value,
				type,
				url: '',
				edit_link: '',
				tree: [],
				expanded: false,
			};

			currentSections = [ ...currentSections, entity ];
			addSectionHeadingEntity( entity ); // Adds to the workspace

			const res = await addSectionHeading( currentID, currentSections );

			if ( 200 === res ) {
				if ( this._isMounted ) {
					this.setState( {
						showForm: false,
						value: '',
					} );
				}
			}
		} else if ( value && 'answer' === type ) {
			const question = this.props.question;

			// Setup a new answer object
			const newAnswer = {
				answer: value,
				html: false,
				points: 0,
				correct: false,
				sortString: '',
				sortStringHtml: false,
				graded: '1',
				gradingProgression: '',
				gradedType: 'text',
				type: 'answer',
				answerType: 'single',
			};

			// Create new answers array for our question type
			const newAnswers = [
				...question.answers[
					LearnDashData.questions_types_map[ question.question_type ]
				],
				newAnswer,
			];

			// Update question
			const res = await updateAnswer( parentLesson, newAnswers );

			if ( 200 === res ) {
				// Update our workspace
				addAnswerEntity( newAnswer, question );

				if ( this._isMounted ) {
					this.setState( {
						showForm: false,
						value: '',
					} );
				}
			}
		}
	}

	/**
	 * Shows the form to add the new entity. First make an AJAX call to retrieve the post id for the new entity.
	 *
	 * @param {handleShowForm} e
	 */
	async handleShowForm( e ) {
		e.preventDefault();
		const { type } = this.props;

		// Adding a lesson, topic, quiz or question
		if ( 'answer' !== type && 'section-heading' !== type ) {
			// Build payload
			const d = new Date();
			const newStepId = `new-step-${ d.getTime() }`;
			const payload = {
				action: 'learndash_builder_selector_step_new',
				builder_data:
					learndash_builder_assets[
						LearnDashData.post_data.builder_post_type
					].post_data,
				builder_query_args: {
					new_steps: {},
				},
			};
			payload.builder_query_args.new_steps[ newStepId ] = {
				item_id: newStepId,
				post_type: LearnDashData.sfwdMap[ type ],
				post_title: LearnDashData.labels[ type ],
			};

			// Post to AJAX endpoint.
			const res = await ajaxRequest( payload, {
				cancelToken: this.signal.token,
			} );

			if ( res.data.status ) {
				this.setState( {
					post_id: res.data.new_steps[ newStepId ].post_id,
					edit_url: res.data.new_steps[ newStepId ].edit_url,
					view_url: res.data.new_steps[ newStepId ].view_url,
				} );
			} else if ( this._isMounted ) {
				this.setState( {
					error: `${ LearnDashData.i18n.error }`,
				} );
			}
		}

		// show the form while we are fetching the id for the new entity in the background
		this.setState( {
			showForm: true,
		} );
	}

	/**
	 * Change to input fields.
	 *
	 * @param {handleChange} e
	 */
	handleChange( e ) {
		e.preventDefault();
		this.setState( {
			value: e.target.value,
		} );
	}

	/**
	 * Cancel button click handler.
	 *
	 * @param {handleCancelAddNew} e
	 */
	handleCancelAddNew( e ) {
		e.preventDefault();

		const { post_id } = this.state;
		const { type } = this.props;

		this.setState( {
			showForm: false,
		} );

		if ( 'answer' !== type && post_id ) {
			// Delete the temporary post created if the user decides to cancel
			deleteEntity( post_id, type );
		}
	}

	/**
	 * Render function
	 *
	 * @event handleSubmit
	 * @event handleShowForm
	 * @event handleChange
	 * @event handleCancelAddNew
	 */
	render() {
		const { showForm, value, is_loading, error } = this.state;
		const { type } = this.props;
		const placeholderText =
			'answer' !== type
				? LearnDashData.i18n.enter_title
				: LearnDashData.i18n.enter_answer;
		return (
			<div
				className={ `ld__builder--new-entity ${
					showForm ? '-expanded' : '-collapsed'
				}` }
			>
				{ ! showForm && (
					<a href="#" onClick={ this.handleShowForm }>
						<Icon icon="plus" />
						{ `${ LearnDashData.i18n.new_element_labels[ type ] }` }
					</a>
				) }

				{ showForm && (
					<div>
						<form onSubmit={ this.handleSubmit }>
							<input
								type="text"
								autoFocus
								value={ value }
								placeholder={ placeholderText }
								onChange={ this.handleChange }
							/>
							{ is_loading && (
								<span className="ld__builder--form-notice">
									{ `${ LearnDashData.i18n.please_wait }` }
								</span>
							) }

							{ ! is_loading && (
								<span>
									<input
										type="submit"
										value={ `${ LearnDashData.i18n.add_element_labels[ type ] }` }
										className="is-primary ld__builder--new-entity-button"
									/>
									<input
										type="button"
										onClick={ this.handleCancelAddNew }
										value={ `${ LearnDashData.i18n.cancel }` }
										className="is-default ld__builder--new-entity-button"
									/>
								</span>
							) }

							{ error && (
								<span className="ld__builder--form-error">
									{ error }
								</span>
							) }
						</form>
					</div>
				) }
			</div>
		);
	}
}

/**
 * Valid props
 */
NewEntity.propTypes = {
	workspace: PropTypes.shape( {
		present: PropTypes.object,
	} ),
	type: PropTypes.string,
	position: PropTypes.string,
	parentNode: PropTypes.number,
	addLesson: PropTypes.func,
	addSectionHeadingEntity: PropTypes.func,
	addTopic: PropTypes.func,
	addQuiz: PropTypes.func,
	addLessonEntity: PropTypes.func,
	addTopicEntity: PropTypes.func,
	addQuizEntity: PropTypes.func,
	addFinalQuizEntity: PropTypes.func,
	addQuestionEntity: PropTypes.func,
	addQuestion: PropTypes.func,
	addAnswerEntity: PropTypes.func,
	setActiveLesson: PropTypes.func,
	parentLesson: PropTypes.number,
	question: PropTypes.object,
	data: PropTypes.shape( {
		labels: PropTypes.object,
	} ),
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 * @param {*} dispatch
 */
const mapDispatchToProps = ( dispatch ) => ( {
	addLesson: ( lesson ) => dispatch( addLesson( lesson ) ),
	addTopic: ( topic ) => dispatch( addTopic( topic ) ),
	addQuiz: ( quiz ) => dispatch( addQuiz( quiz ) ),
	addQuestion: ( question ) => addQuestion( addLesson( question ) ),
	addLessonEntity: ( lesson ) => dispatch( addLessonEntity( lesson ) ),
	addSectionHeadingEntity: ( entity ) =>
		dispatch( addSectionHeadingEntity( entity ) ),
	addTopicEntity: ( parent, destination, topic ) =>
		dispatch( addTopicEntity( parent, destination, topic ) ),
	addQuizEntity: ( parent, destination, quiz ) =>
		dispatch( addQuizEntity( parent, destination, quiz ) ),
	addFinalQuizEntity: ( index, quiz ) =>
		dispatch( addFinalQuizEntity( index, quiz ) ),
	addQuestionEntity: ( question ) =>
		dispatch( addQuestionEntity( question ) ),
	addAnswerEntity: ( answer, parent ) =>
		dispatch( addAnswerEntity( answer, parent ) ),
	setActiveLesson: ( id ) => dispatch( setActiveLesson( id ) ),
} );

export default connect( mapStateToProps, mapDispatchToProps )( NewEntity );
